import { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, ArrowRight, X, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const galleryData = {
  "Paver Patios": [
    {
      url: "https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/dx0a3wdq_IMG_4852.jpeg",
      alt: "Paver patio with stone pillars and lighting"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/btl73w4y_IMG_0436.jpeg",
      alt: "Circular fire pit paver patio by the lake"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/bp8uwxqn_IMG_2279.jpeg",
      alt: "Backyard paver patio with decorative border"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/4u9w80e9_IMG_0718.jpeg",
      alt: "Commercial paver walkway"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/6ukm95zo_IMG_1454.jpeg",
      alt: "Lakefront paver patio with retaining wall"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/zydjz8ob_IMG_1430.jpeg",
      alt: "Paver patio installation"
    }
  ],
  "Landscaping": [
    {
      url: "https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/cz3w10y4_Screenshot%202026-02-25%20at%203.46.31%E2%80%AFPM.png",
      alt: "Arborvitae tree row installation"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/tpx083x2_Screenshot%202026-02-25%20at%203.46.39%E2%80%AFPM.png",
      alt: "Professional lawn striping"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/51zgzflw_Screenshot%202026-02-25%20at%203.46.56%E2%80%AFPM.png",
      alt: "Front yard landscaping with mulch beds"
    }
  ],
  "Retaining Walls": [
    {
      url: "https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/byhpznjq_IMG_1517.png",
      alt: "Front yard landscaping with retaining wall and mulch beds"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/ja35jvxr_Screenshot%202026-02-25%20at%203.46.46%E2%80%AFPM.png",
      alt: "Retaining wall with landscaping"
    }
  ],
  "Concrete": [
    {
      url: "https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/4kmk778h_Screenshot%202026-02-25%20at%203.47.03%E2%80%AFPM.png",
      alt: "Concrete patio installation"
    }
  ],
  "Snow Removal": [
    {
      url: "https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/1l72nh65_IMG_0205.jpeg",
      alt: "Snow plow truck in action"
    }
  ]
};

export default function GalleryPage() {
  const [lightbox, setLightbox] = useState({ isOpen: false, images: [], currentIndex: 0 });

  const openLightbox = (category, index) => {
    setLightbox({
      isOpen: true,
      images: galleryData[category],
      currentIndex: index,
      category
    });
  };

  const closeLightbox = () => {
    setLightbox({ isOpen: false, images: [], currentIndex: 0 });
  };

  const nextImage = () => {
    setLightbox(prev => ({
      ...prev,
      currentIndex: (prev.currentIndex + 1) % prev.images.length
    }));
  };

  const prevImage = () => {
    setLightbox(prev => ({
      ...prev,
      currentIndex: (prev.currentIndex - 1 + prev.images.length) % prev.images.length
    }));
  };

  return (
    <div data-testid="gallery-page">
      {/* Page Header */}
      <section className="bg-[#F9FAFB] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-[#2F5930] font-semibold mb-3 tracking-wide uppercase text-sm">
              Our Work
            </p>
            <h1 className="text-4xl md:text-5xl font-bold text-[#1F2937] mb-6 font-['Manrope']">
              Project Gallery
            </h1>
            <p className="text-lg text-[#4B5563] leading-relaxed">
              Browse through our completed landscaping, paver patio, retaining wall, and hardscape 
              projects throughout Joliet, IL and surrounding areas.
            </p>
          </div>
        </div>
      </section>

      {/* Gallery Sections */}
      {Object.entries(galleryData).map(([category, images]) => (
        <section 
          key={category} 
          className="py-16 md:py-20 border-b border-gray-100 last:border-b-0"
          data-testid={`gallery-section-${category.toLowerCase().replace(" ", "-")}`}
        >
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-[#1F2937] font-['Manrope']">
                {category}
              </h2>
              <span className="text-[#2F5930] font-medium">
                {images.length} {images.length === 1 ? 'Project' : 'Projects'}
              </span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => openLightbox(category, index)}
                  className="group relative aspect-square rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
                >
                  <img
                    src={image.url}
                    alt={image.alt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                    <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 font-medium">
                      View
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* CTA Section */}
      <section className="bg-[#2F5930] py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-['Manrope']">
            Ready to Start Your Project?
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Let us transform your outdoor space. Contact us today for a free estimate.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="tel:8159098790">
              <Button 
                size="lg"
                className="bg-white text-[#2F5930] hover:bg-gray-100 rounded-full px-8"
              >
                <Phone className="w-5 h-5 mr-2" />
                (815) 909-8790
              </Button>
            </a>
            <Link to="/contact">
              <Button 
                size="lg"
                variant="outline"
                className="bg-transparent text-white border-2 border-white hover:bg-white/10 rounded-full px-8"
              >
                Get Free Estimate
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightbox.isOpen && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-gray-300 z-50"
          >
            <X className="w-8 h-8" />
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            className="absolute left-4 text-white hover:text-gray-300 z-50"
          >
            <ChevronLeft className="w-10 h-10" />
          </button>

          <div 
            className="max-w-5xl max-h-[80vh] mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightbox.images[lightbox.currentIndex]?.url}
              alt={lightbox.images[lightbox.currentIndex]?.alt}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
            <div className="text-center mt-4">
              <p className="text-white/80">
                {lightbox.category} - {lightbox.currentIndex + 1} of {lightbox.images.length}
              </p>
            </div>
          </div>

          <button
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            className="absolute right-4 text-white hover:text-gray-300 z-50"
          >
            <ChevronRight className="w-10 h-10" />
          </button>
        </div>
      )}
    </div>
  );
}
